import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Users, Wrench, CalendarClock, CheckCircle2, AlertCircle, Activity, ArrowRight, Printer, ShoppingBag, Banknote, ShieldAlert, Phone } from "lucide-react";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [warrantyAlerts, setWarrantyAlerts] = useState([]);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/admin/repairs").then((r) => setRecent(r.data.slice(0, 5)));
    api.get("/admin/warranty-alerts").then((r) => setWarrantyAlerts(r.data)).catch(() => {});
  }, []);

  const cards = stats ? [
    { icon: Users, label: "Clienti", value: stats.total_customers, color: "#00E5FF", to: "/admin/clienti" },
    { icon: Wrench, label: "Total reparatii", value: stats.total_repairs, color: "#00E5FF", to: "/admin/reparatii" },
    { icon: Activity, label: "Reparatii active", value: stats.active_repairs, color: "#f59e0b", to: "/admin/reparatii" },
    { icon: CheckCircle2, label: "Finalizate", value: stats.completed_repairs, color: "#22c55e", to: "/admin/reparatii" },
    { icon: AlertCircle, label: "Rezervari noi", value: stats.new_reservations, color: "#00E5FF", to: "/admin/rezervari" },
    { icon: CalendarClock, label: "Total rezervari", value: stats.total_reservations, color: "#737373", to: "/admin/rezervari" },
    { icon: ShoppingBag, label: "Comenzi magazin", value: stats.shop_orders_total ?? 0, color: "#a855f7", to: "/admin/comenzi" },
    { icon: AlertCircle, label: "Comenzi noi", value: stats.shop_orders_new ?? 0, color: "#f59e0b", to: "/admin/comenzi" },
    { icon: Banknote, label: "Incasari magazin (RON)", value: Math.round(stats.shop_revenue ?? 0), color: "#22c55e", to: "/admin/comenzi" },
  ] : [];

  const STATUS_RO = {
    received: { label: "Primit", color: "bg-blue-500" },
    in_progress: { label: "In lucru", color: "bg-amber-500" },
    completed: { label: "Finalizat", color: "bg-green-500" },
    delivered: { label: "Predat", color: "bg-neutral-500" },
  };

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-display text-4xl uppercase">Dashboard</h1>
      <p className="text-sm text-neutral-400 mb-10">Status atelier in timp real</p>

      {warrantyAlerts.length > 0 && (
        <div data-testid="warranty-alerts-panel" className="mb-8 border border-amber-500/50 bg-amber-950/20">
          <div className="flex items-center gap-2 px-5 py-3 border-b border-amber-500/30">
            <ShieldAlert className="w-5 h-5 text-amber-400" />
            <h3 className="font-display text-lg uppercase text-amber-400">Garantii service care expira in 7 zile</h3>
            <span className="ml-auto bg-amber-500 text-black text-xs font-bold px-2 py-0.5">{warrantyAlerts.length}</span>
          </div>
          <div className="divide-y divide-amber-500/15">
            {warrantyAlerts.map((w) => (
              <div key={w.id} data-testid={`warranty-alert-${w.id}`} className="flex flex-wrap items-center gap-3 px-5 py-3">
                <span className="font-mono text-[10px] uppercase px-2 py-1 bg-amber-500/10 text-amber-400 border border-amber-500/30">#{w.ticket}</span>
                <div className="flex-1 min-w-[180px] text-sm">
                  <strong className="text-white">{w.customer_name}</strong>
                  <span className="text-neutral-400"> · {w.scooter_model}</span>
                  {w.warranty_items && <span className="text-neutral-500"> · {w.warranty_items}</span>}
                </div>
                <span className={`text-xs font-bold uppercase tracking-wider ${w.days_left <= 2 ? "text-red-400" : "text-amber-400"}`}>
                  {w.days_left === 0 ? "Expira azi!" : `${w.days_left} ${w.days_left === 1 ? "zi ramasa" : "zile ramase"}`}
                </span>
                <span className="text-xs text-neutral-500">pana la {new Date(w.warranty_until).toLocaleDateString("ro-RO")}</span>
                <a href={`tel:${w.customer_phone}`} className="inline-flex items-center gap-1.5 text-xs text-[#00E5FF] hover:underline">
                  <Phone className="w-3.5 h-3.5" /> {w.customer_phone}
                </a>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map((c, i) => (
          <Link
            to={c.to}
            key={i}
            data-testid={`stat-card-${i}`}
            className="bg-neutral-950 border border-neutral-800 p-6 hover:border-[#00E5FF] transition-colors group"
          >
            <div className="flex items-start justify-between mb-4">
              <c.icon className="w-7 h-7" style={{ color: c.color }} />
              <ArrowRight className="w-4 h-4 text-neutral-700 group-hover:text-[#00E5FF] group-hover:translate-x-1 transition-all" />
            </div>
            <div className="font-display text-5xl text-white">{c.value}</div>
            <div className="text-xs uppercase tracking-widest text-neutral-500 mt-2">{c.label}</div>
          </Link>
        ))}
      </div>

      <div className="mt-12 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-neutral-950 border border-neutral-800 p-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-display text-xl uppercase">Vanzari magazin · ultimele luni</h3>
            <Link to="/admin/comenzi" className="text-xs text-[#00E5FF] uppercase tracking-widest hover:underline">Comenzi →</Link>
          </div>
          {(!stats?.shop_monthly || stats.shop_monthly.length === 0) ? (
            <div className="text-sm text-neutral-500 py-8 text-center">Nicio vanzare inca in magazin</div>
          ) : (
            <div data-testid="shop-sales-chart" className="flex items-end gap-4 h-48 pt-4">
              {stats.shop_monthly.map((m) => {
                const max = Math.max(...stats.shop_monthly.map((x) => x.revenue), 1);
                return (
                  <div key={m.month} className="flex-1 flex flex-col items-center gap-2 h-full justify-end">
                    <div className="text-xs font-bold text-[#00E5FF]">{Math.round(m.revenue)}</div>
                    <div className="w-full bg-[#00E5FF] hover:bg-[#5CEEFF] transition-colors tx-cyan-glow"
                      style={{ height: `${Math.max(6, (m.revenue / max) * 100)}%` }}
                      title={`${m.count} comenzi · ${Math.round(m.revenue)} RON`} />
                    <div className="text-[10px] uppercase tracking-wider text-neutral-500">{m.month.slice(5)}/{m.month.slice(2, 4)}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="bg-[#00E5FF] text-black p-6">
          <h3 className="font-display text-xl uppercase mb-3">Cum lucrezi rapid</h3>
          <ol className="text-sm space-y-2 list-decimal pl-4">
            <li>Vine client → <strong>Reparatie noua</strong></li>
            <li>Apesi <strong>Printeaza</strong> → 2 foi (atelier + contract)</li>
            <li>Clientul semneaza contractul</li>
            <li>Cand e gata → <strong>Finalizeaza</strong></li>
            <li>SMS automat catre client</li>
          </ol>
        </div>
      </div>

      <div className="mt-6 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-neutral-950 border border-neutral-800 p-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-display text-xl uppercase">Ultimele reparatii</h3>
            <Link to="/admin/reparatii" className="text-xs text-[#00E5FF] uppercase tracking-widest hover:underline">Vezi toate →</Link>
          </div>
          {recent.length === 0 && <div className="text-sm text-neutral-500 py-8 text-center">Nicio reparatie inca</div>}
          <div className="space-y-2">
            {recent.map((r) => {
              const s = STATUS_RO[r.status] || STATUS_RO.received;
              const ticket = r.id.slice(0, 8).toUpperCase();
              return (
                <div key={r.id} className="flex items-center gap-3 p-3 bg-neutral-900 hover:bg-neutral-900/60 border border-neutral-800 hover:border-[#00E5FF]/50 transition-colors">
                  <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 bg-[#00E5FF]/10 text-[#00E5FF] flex-shrink-0">#{ticket}</span>
                  <span className={`text-[10px] uppercase tracking-widest text-white px-2 py-0.5 ${s.color} flex-shrink-0`}>{s.label}</span>
                  <div className="flex-1 min-w-0 text-sm truncate">
                    <strong className="text-white">{r.customer_name}</strong>
                    <span className="text-neutral-500"> · {r.scooter_model}</span>
                  </div>
                  <button
                    onClick={() => window.open(`/admin/reparatii/${r.id}/print`, "_blank")}
                    className="text-neutral-500 hover:text-[#00E5FF] flex-shrink-0"
                    title="Printeaza"
                  >
                    <Printer className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
